import {Authenticator, useAuthenticator, Button, Alert, Tabs}        from "@aws-amplify/ui-react";
import {
    confirmSignUp, signIn, SignInInput, SignInOutput,
    resendSignUpCode,
    ConfirmSignUpInput,
    ConfirmSignUpOutput,
    ResendSignUpCodeInput,
    ResendSignUpCodeOutput,
}                                                                    from "@aws-amplify/auth";
import {Modal}                                                       from "react-bootstrap";
import {I18n}                                                        from 'aws-amplify/utils';
import {accessSearchDetailsPrompt, defaultHeader, termsSignUpFooter} from "../UIElements";
import {useUser}                                                     from "../../context/UserProvider/UserProvider";
import React, {useEffect, useState}                                  from "react";
import {
    DefaultComponents
}                                                                    from "@aws-amplify/ui-react/dist/types/components/Authenticator/hooks/useCustomComponents/defaultComponents";
import {encryptData, decryptData}                                    from "../../common/cryptoUtils";

I18n.setLanguage('en');
I18n.putVocabularies({
                         en: {
                             'Sign In'                                  : 'Log In',
                             'Username/client id combination not found.': 'We couldn\'t find an account associated with that email address.',
                         },
                     });


const AuthModal = () => {
    const {authModalShow, setAuthModalShow, authInitialState} = useUser();
    const handleClose = () => {
        setAuthModalShow(null);
    };
    const [resendCodeStatus, setResendCodeStatus] = useState('Resend Code');
    const [confirmStatus, setConfirmStatus] = useState('Confirm');
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const services = {

        async handleSignIn(input: SignInInput): Promise<SignInOutput> {
            try {
                const user = await signIn(input);
                if (user.nextStep.signInStep === 'CONFIRM_SIGN_UP') {
                    console.log("user not verified, redirecting to confirm sign up")
                    const encryptedInput = encryptData(JSON.stringify(input));
                    sessionStorage.setItem('loginInput', encryptedInput);
                }
                return user;
            }
            catch (error) {
                console.error("handleSignIn error: ", error);
                throw error;
            }
        },

        async handleConfirmSignUp(input: ConfirmSignUpInput): Promise<ConfirmSignUpOutput> {
            const {username, confirmationCode} = input;
            setConfirmStatus('Confirming...');
            try {
                const encryptedInput = sessionStorage.getItem('loginInput');
                const loginInput = encryptedInput ? JSON.parse(decryptData(encryptedInput)) : null;
                const result = await confirmSignUp({username, confirmationCode});
                if (loginInput) {
                    await signIn(loginInput);
                }
                return result;
            }
            catch (error) {
                console.error(error);
                throw error;
            }
            finally {
                sessionStorage.removeItem('loginInput');
                setConfirmStatus('Confirm');
            }
        },

        async handleResendSignUpCode({username}: ResendSignUpCodeInput): Promise<ResendSignUpCodeOutput | undefined> {
            setResendCodeStatus('Resending...');
            setIsButtonDisabled(true);
            try {
                const result = await resendSignUpCode({username})
                setIsButtonDisabled(false);
                return result;
            }
            catch (AmplifyError: any) {
                console.error(AmplifyError);
                if (AmplifyError.name === 'LimitExceededException') {
                    console.error('Limit for verification code to be resent exceeded. Please try after some time.');
                    setErrorMessage('Limit for verification code to be resent exceeded. Please try after some time.');
                }
                else {
                    console.error(AmplifyError);
                    alert(`An error occurred: ${AmplifyError.message}`);
                    setErrorMessage(`An error occurred: ${AmplifyError.message}`);
                }

            }
            finally {
                setResendCodeStatus('Resend Code');

            }
        }
    };

    const CustomResendCodeButton = ({onClick, resendCodeStatus, disabled}: any) => (
        <Button onClick={onClick} disabled={disabled}
                className="w-100">{resendCodeStatus}</Button>
    );

    const ErrorMessage = ({message}: any) => (
        <Alert isDismissible={true} variation={'error'} className={"pt-3"}>
            {message}
        </Alert>
    );

    const CustomConfirmSignUpFooter = () => {
        const {username} = useAuthenticator();
        const handleResendClick = () => {
            services.handleResendSignUpCode({username});
        };

        return (
            <div>
                <CustomResendCodeButton
                    onClick={handleResendClick}
                    resendCodeStatus={resendCodeStatus}
                    disabled={isButtonDisabled}
                />
                {errorMessage && <ErrorMessage message={errorMessage}/>}
                <div className="verify_check_spam_msg" style={{fontSize: '90%', marginTop: '10px'}}>If you don’t see the
                    email within 2
                    minutes,
                    please
                    check your
                    spam folder, and if it’s there, please mark it as not spam.
                </div>
            </div>
        );
    };
    const CustomConfirmSignUpHeader = () => (
        <div>
            <h3>Verify Your Account</h3>
            <p>We’ve sent a confirmation code to your email. Please enter it below to complete registration.</p>
        </div>
    );

    const CustomSignUpHeader = () => {
        const {toSignIn} = useAuthenticator(); // Get the navigation functions for the Authenticator

        // Handler function to switch to the Login tab (SignIn state)
        const handleLoginClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
            event.preventDefault(); // Prevent default link behavior
            toSignIn(); // Switch to the Login (SignIn) state
        };

        return (
            <div className="p-4 pt-2 text-center">
                Create a new account for free. <br/>
                Already registered?{' '}
                <a href="#" onClick={handleLoginClick}>
                    Login
                </a>
            </div>
        );
    };
    const CustomLoginHeader = () => {
        const {toSignUp} = useAuthenticator(); // Access the Authenticator controls

        // Function to switch to the Sign Up state
        const handleSignUpClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
            event.preventDefault(); // Prevent default link behavior
            toSignUp(); // Switch to the Sign Up state
        };

        return (
            <div className="p-4 pt-2 text-center">
                <span className='fa-bold'>Welcome back!</span> <br/>
                Don't have an account?{' '}
                <a href="#" onClick={handleSignUpClick}>
                    Sign Up
                </a>
            </div>
        );
    };

    const DynamicHeader = () => {
        const {route} = useAuthenticator();
        let headerContent;

        switch (route) {
            case 'signUp':
                headerContent = (
                    <div className='text-center ms-2 me-2 pt-1 pb-5 lead fs-3'>Sign up for free to
                        access over 1 million human correlations!</div>
                );
                break;
            case 'signIn':
                headerContent = (
                    <div
                        className='text-center ms-2 me-2 pt-1 pb-5 lead fs-3'>Login to
                        access over 1 million human correlations!</div>
                );
                break;
            default:
                headerContent = (
                    <div className='text-center ms-2 me-2 pt-1 pb-3 lead fs-4'>Sign up for free or login to access
                        over&nbsp;1&nbsp;million human correlations!</div>
                );

        }
        return headerContent;
    }

    const CustomSignUpFooter = () => {
        const {toSignIn} = useAuthenticator();
        const handleSignInClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
            event.preventDefault(); // Prevent default link behavior
            toSignIn(); // Switch to the Sign Up state
        };
        return (
            <div>
                <div className="text-center pb-3">Already registered?{' '}
                    <a href="#" onClick={handleSignInClick}>
                        Login
                    </a></div>
                <div className="small p-4 pt-2">By creating an account you are agreeing to our <a
                    href="https://personalitymap.io/terms" target='_blank'>Terms of
                    Service</a> and <a href='https://personalitymap.io/privacy-policy'
                                       target='_blank'>Privacy
                    Policy</a>. We may also send you occasional email updates, which you can unsubscribe from easily
                    with
                    one click.
                </div>
            </div>

        )
    }


    const getComponents = (): DefaultComponents | undefined => {
        if (authModalShow) {
            const header = defaultHeader.Header;

            return {
                Header: DynamicHeader,
                SignUp: {
                    Header: () => {return null},
                    Footer: CustomSignUpFooter,
                },
                SignIn: {
                    Header: CustomLoginHeader, // Use CustomLoginHeader for Login (Sign In) state
                },

                ConfirmSignUp: {
                    Footer: CustomConfirmSignUpFooter,
                },
            };
        }

        return undefined;
    };

    return (
        <Modal
            centered
            backdrop="static"
            className="authModal"
            size={"lg"}
            show={!!authModalShow}
            onHide={handleClose} id="authModal"
        >
            <Modal.Header closeButton>
                <Modal.Title id="contained-modal-title-vcenter"></Modal.Title>
            </Modal.Header>
            <Modal.Body className={"p-0"}>
                {!!authModalShow && (
                    <div className="authenticator-custom-container">
                        <Authenticator
                            initialState={authInitialState}
                            signUpAttributes={['email']}
                            services={services}
                            formFields={{
                                signUp: {
                                    email           : {
                                        order     : 1,
                                        isRequired: true,
                                        type      : 'email',
                                        pattern   : "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$"
                                    },
                                    password        : {order: 2},
                                    confirm_password: {order: 3},
                                },
                            }}
                            components={getComponents()}
                        /></div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <div className="pb-5 pt-3"></div>
            </Modal.Footer>
        </Modal>
    );
};

export default AuthModal;